/* @flow */

import type { NETGEM_API_V8_FEED_ITEM, NETGEM_API_V8_FEED_RAW_ITEM, NETGEM_API_V8_ITEM_LOCATION } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_PARENTAL_GUIDANCE } from '../../../libs/netgemLibrary/v8/types/ParentalGuidance';
import type { EXTENDED_ITEM } from './types';

import {
  type NetgemApiV8WidgetItemClick,
  NetgemApiV8WidgetItemClickActionKind,
  type NetgemApiV8WidgetItemClickParam,
  type NetgemApiV8WidgetItemClickParams,
} from '../../../libs/netgemLibrary/v8/types/WidgetConfig';

const getParentalGuidanceFromItem: (item: NETGEM_API_V8_FEED_ITEM) => NETGEM_API_V8_PARENTAL_GUIDANCE | null = (item) => {
  const { selectedProgramId } = item;
  const q: Array<NETGEM_API_V8_FEED_ITEM | NETGEM_API_V8_FEED_RAW_ITEM | NETGEM_API_V8_ITEM_LOCATION> = [item];

  while (q.length > 0) {
    const current = q.pop();

    if (current) {
      const { elements, id, locations, parentalGuidance } = current;

      if (id === selectedProgramId) {
        return parentalGuidance ?? null;
      }

      if (locations) {
        locations.forEach((l) => q.push(l));
      }

      if (elements) {
        elements.forEach((e) => q.push(e));
      }
    }
  }

  // Not found
  return null;
};

const getValueFromPrefix: (item: any, prefix: string) => any = (item, prefix) => {
  const q = [item];

  while (q.length > 0) {
    const o = q.pop();
    if (o) {
      if (typeof o === 'object') {
        Object.entries(o).forEach(([k, v]) => {
          if (k !== 'elements' && (typeof v === 'string' || typeof v === 'object')) {
            q.push(v);
          }
        });
      } else if (o.startsWith(prefix)) {
        // Value found
        return o;
      }
    }
  }

  // Not found
  return null;
};

const getOnItemClick: (fullItem: EXTENDED_ITEM, onItemClick: NetgemApiV8WidgetItemClick) => NetgemApiV8WidgetItemClick | null = (fullItem, onItemClick) => {
  const { action, params } = onItemClick;
  const { item, seriesMetadata } = fullItem;

  if (action !== NetgemApiV8WidgetItemClickActionKind.Switch || params?.uri !== undefined) {
    // Directive onClick is not a switch, but an action
    return onItemClick;
  }

  // Action: switch
  const switchActionParams = params as any as NetgemApiV8WidgetItemClickParams;

  if (!switchActionParams) {
    // Missing data:
    return null;
  }

  const {
    id,
    selectedLocation: { id: locationId },
    seriesId,
  } = item;

  const assetSeriesId = seriesMetadata?.id ?? seriesId;

  for (const switchItem: NetgemApiV8WidgetItemClickParam of switchActionParams) {
    const { action: switchItemAction, prefix } = switchItem;
    if (!prefix || id.startsWith(prefix) || assetSeriesId?.startsWith(prefix) || locationId?.startsWith(prefix)) {
      if (switchItemAction.action === NetgemApiV8WidgetItemClickActionKind.Switch) {
        // Recursive call to retrieve action
        return getOnItemClick(fullItem, switchItemAction as any as NetgemApiV8WidgetItemClick);
      }

      // Action found (switchItemAction.action is either 'none', 'card1' or 'hub1')
      return switchItemAction as any as NetgemApiV8WidgetItemClick;
    }
  }

  // No action found
  return null;
};

export { getOnItemClick, getParentalGuidanceFromItem, getValueFromPrefix };
